import { useEffect, useRef } from "react";
import { isEqual } from "lodash";
import { useValidatorContext } from "./provider";
import type { ExtendedValidationFieldConfig } from "./types";

/**
 * Stabilizes a config object reference using deep comparison.
 * Prevents unnecessary useEffect re-runs when config is structurally identical
 * but referentially different (e.g. inline object literals on every render).
 */
const useStableConfig = (config: ExtendedValidationFieldConfig): ExtendedValidationFieldConfig => {
    const ref = useRef(config);
    if (!isEqual(ref.current, config)) {
        ref.current = config;
    }
    return ref.current;
};

export const useValidation = (fieldId: string, config: ExtendedValidationFieldConfig) => {
    const { attachFieldValidation } = useValidatorContext();
    const stableConfig = useStableConfig(config);

    useEffect(() => {
        if (fieldId) {
            const cleanup = attachFieldValidation(fieldId, stableConfig);
            return cleanup;
        }
    }, [attachFieldValidation, stableConfig, fieldId]);
};
